import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { RequiredFile, UpdateRequiredFileRequest, AccountFilter } from '../../types';
import { FILE_TYPES } from '../../constants/constants';
import { SearchableSelect } from '../../components/SearchableSelect';
import { normalizeFileType, denormalizeFileType } from '../../utils/fileTypeUtils';

interface EditRequiredFileModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (requirement_id: string, updateData: UpdateRequiredFileRequest) => void;
  requirement: RequiredFile | null;
}

const EditRequiredFileModal: React.FC<EditRequiredFileModalProps> = ({ 
  show, 
  onHide, 
  onSubmit, 
  requirement 
}) => {
  const [validated, setValidated] = useState(false);
  const [selectedFileTypeUI, setSelectedFileTypeUI] = useState<string>(''); // UI display name
  const [formData, setFormData] = useState<UpdateRequiredFileRequest>({
    category: '',
    file_type: '',
    description: '',
    required_count: 1,
    notes: ''
  });

  const [showDateRange, setShowDateRange] = useState(false);
  const [showAccountFilter, setShowAccountFilter] = useState(false);
  const [showCheckNumbers, setShowCheckNumbers] = useState(false);
  const [checkNumbersInput, setCheckNumbersInput] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [accountFilter, setAccountFilter] = useState<AccountFilter>({});

  // Populate form when requirement changes
  useEffect(() => {
    if (show && requirement) {
      // Denormalize file_type for display
      const uiFileType = denormalizeFileType(requirement.file_type);
      setSelectedFileTypeUI(uiFileType);
      
      setFormData({
        category: requirement.category,
        file_type: requirement.file_type, // Keep normalized for internal use
        description: requirement.description,
        required_count: requirement.required_count || 1,
        notes: requirement.notes || ''
      });
      
      if (requirement.date_range) {
        setDateRange({
          from: requirement.date_range.from.split('T')[0], // Extract date part
          to: requirement.date_range.to.split('T')[0]
        });
        setShowDateRange(true);
      } else {
        setDateRange({ from: '', to: '' });
        setShowDateRange(false);
      }
      
      if (requirement.account_filter) {
        setAccountFilter(requirement.account_filter);
        setShowAccountFilter(true);
      } else {
        setAccountFilter({});
        setShowAccountFilter(false);
      }
      
      if (requirement.check_numbers && requirement.check_numbers.length > 0) {
        setCheckNumbersInput(requirement.check_numbers.join(', '));
        setShowCheckNumbers(true);
      } else {
        setCheckNumbersInput('');
        setShowCheckNumbers(false);
      }
      
      setValidated(false);
    }
  }, [show, requirement]);

  // Update normalized file_type and showDateRange when UI selection changes
  useEffect(() => {
    if (selectedFileTypeUI) {
      const normalized = normalizeFileType(selectedFileTypeUI);
      setFormData(prev => ({ ...prev, file_type: normalized }));
      
      // Show date range for bank statements
      if (normalized === 'bank_statement') {
        setShowDateRange(true);
      } else if (normalized === 'check_image') {
        setShowCheckNumbers(true);
      } else {
        setShowDateRange(false);
        if (normalized !== 'bank_statement') {
          setDateRange({ from: '', to: '' });
        }
        setShowCheckNumbers(false);
        setCheckNumbersInput('');
      }
    } else {
      setFormData(prev => ({ ...prev, file_type: '' }));
      setShowDateRange(false);
      setDateRange({ from: '', to: '' });
      setShowCheckNumbers(false);
      setCheckNumbersInput('');
    }
  }, [selectedFileTypeUI]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const form = e.currentTarget as HTMLFormElement;
    if (!form.checkValidity() || !selectedFileTypeUI) {
      setValidated(true);
      if (!selectedFileTypeUI) {
        return;
      }
    }

    if (!requirement) return;

    // Normalize the file type before submitting
    const normalizedFileType = normalizeFileType(selectedFileTypeUI);
    
    // Derive category from file type
    let category = 'Other';
    if (selectedFileTypeUI === 'Bank Statements' || normalizedFileType === 'bank_statement') {
      category = 'Bank Statements';
    } else if (selectedFileTypeUI === 'Check Images' || normalizedFileType === 'check_image') {
      category = 'Supporting Documents';
    } else if (selectedFileTypeUI.startsWith('IRS Tax form') || normalizedFileType.startsWith('irs_tax_form')) {
      category = 'Financial Records';
    } else if (selectedFileTypeUI.startsWith('Financial statement') || normalizedFileType.startsWith('financial_statement')) {
      category = 'Financial Records';
    } else if (normalizedFileType === 'invoice' || normalizedFileType === 'expense_report') {
      category = 'Financial Records';
    } else if (normalizedFileType === 'legal_document' || normalizedFileType === 'lease_agreement') {
      category = 'Legal Documents';
    } else {
      // Keep existing category if we can't derive it
      category = formData.category || 'Other';
    }

    const updateData: UpdateRequiredFileRequest = {
      category: category,
      file_type: normalizedFileType,
      description: formData.description.trim(),
    };

    // Add date_range for bank statements
    if (formData.file_type === 'bank_statement' && dateRange.from && dateRange.to) {
      updateData.date_range = dateRange;
    } else {
      // Remove date_range if not bank statement
      updateData.date_range = undefined;
    }

    // Add required_count for non-bank-statement files
    if (formData.file_type !== 'bank_statement') {
      updateData.required_count = formData.required_count || 1;
    } else {
      updateData.required_count = undefined;
    }

    // Add account_filter if provided
    if (showAccountFilter && (accountFilter.bank_name || accountFilter.account_number)) {
      updateData.account_filter = accountFilter;
    } else {
      updateData.account_filter = undefined;
    }

    // Add check_numbers for check images if provided
    if (showCheckNumbers && checkNumbersInput && checkNumbersInput.trim()) {
      const parsed = checkNumbersInput
        .split(',')
        .map(s => s.trim())
        .filter(s => s !== '');
      if (parsed.length > 0) {
        updateData.check_numbers = parsed;
      } else {
        updateData.check_numbers = undefined;
      }
    } else {
      updateData.check_numbers = undefined;
    }

    // Add notes if provided
    if (formData.notes && formData.notes.trim()) {
      updateData.notes = formData.notes.trim();
    } else {
      updateData.notes = undefined;
    }

    onSubmit(requirement.requirement_id, updateData);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Required File</Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>File Type <span className="text-danger">*</span></Form.Label>
            <SearchableSelect
              value={selectedFileTypeUI}
              options={FILE_TYPES}
              onChange={setSelectedFileTypeUI}
              placeholder="Type to search file types..."
            />
            {validated && !selectedFileTypeUI && (
              <div className="invalid-feedback d-block">
                Please select a file type
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Monthly bank statements for Q1 2024"
            />
            <Form.Control.Feedback type="invalid">
              Please enter a description
            </Form.Control.Feedback>
          </Form.Group>

          {/* Date Range for Bank Statements */}
          {formData.file_type === 'bank_statement' && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Form.Check
                type="checkbox"
                label="Specify Date Range"
                checked={showDateRange}
                onChange={(e) => setShowDateRange(e.target.checked)}
              />
              {showDateRange && (
                <div className="row mt-2">
                  <div className="col-md-6">
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateRange.from}
                      onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateRange.to}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Required Count for Non-Bank-Statement Files */}
          {formData.file_type && formData.file_type !== 'bank_statement' && (
            <Form.Group className="mb-3">
              <Form.Label>Required Count</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.required_count || 1}
                onChange={(e) => setFormData({ ...formData, required_count: parseInt(e.target.value) || 1 })}
              />
              <Form.Text className="text-muted">
                Number of files needed to satisfy this requirement
              </Form.Text>
            </Form.Group>
          )}

          {/* Account Filter */}
          {formData.file_type === 'bank_statement' && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Form.Check
                type="checkbox"
                label="Add Account Filter (Optional)"
                checked={showAccountFilter}
                onChange={(e) => setShowAccountFilter(e.target.checked)}
              />
              {showAccountFilter && (
                <div className="row mt-2">
                  <div className="col-md-6">
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={accountFilter.bank_name || ''}
                      onChange={(e) => setAccountFilter({ ...accountFilter, bank_name: e.target.value })}
                      placeholder="e.g., Chase Bank"
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Account Number (Last 4 digits)</Form.Label>
                    <Form.Control
                      type="text"
                      value={accountFilter.account_number || ''}
                      onChange={(e) => setAccountFilter({ ...accountFilter, account_number: e.target.value })}
                      placeholder="e.g., 1234"
                      maxLength={4}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes or instructions"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Update Requirement
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditRequiredFileModal;

